import { Minus, Pause, Play, Ruler } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { collectXValues, formatDateAxisValue, seriesDisplayLabel } from './dataviz-utils';
import { fmtLocale, speakNumber } from './number-format';

import type { DataVizBlockData, ResolvedChartSeries } from '@agavi/blocks';

import { Button } from '@/components/ui/button';
import SelectMenu from '@/components/ui/select-menu';
import { playbackSpeedOptions } from '@/lib/playback-speed';

const LOW_HZ = 220; // A3
const HIGH_HZ = 880; // A5
const MANUAL_PREVIEW_DURATION = 0.35;
const AUTO_PLAY_NOTE_DURATION = 0.2;
const CALIBRATION_TONE_DURATION = 0.35;
const CALIBRATION_GAP = 0.15;
// Both threshold clicks are deliberately distinct from the data tones AND from each other — data
// notes are sine waves whose pitch carries the value; these are fixed-pitch percussive clicks
// layered on top, so a listener can tell "this crossed the Y reference" apart from "this crossed
// the X reference" apart from "this is what the value sounds like". Different waveform AND
// different pitch register for the two thresholds (not just one or the other), since either alone
// risks being too subtle to tell apart quickly during playback.
const Y_THRESHOLD_FREQ = 1000;
const Y_THRESHOLD_WAVE: OscillatorType = 'triangle';
const X_THRESHOLD_FREQ = 600;
const X_THRESHOLD_WAVE: OscillatorType = 'square';
const THRESHOLD_DURATION = 0.08;
// Bookends the WHOLE playback session (not any one point) — an ascending two-note chime at the
// very start, a descending one at the very end. Sawtooth (not used anywhere else) and a pitch pair
// outside both the data range (220-880Hz) and the threshold clicks (600/1000Hz) keeps it from ever
// being mistaken for a data point or a threshold crossing. This exists specifically so silence
// during playback — before the first point, between widely-separated clusters, after the last
// point — reads as "the timeline is still running" rather than "something broke."
const SESSION_CHIME_LOW_HZ = 500;
const SESSION_CHIME_HIGH_HZ = 950;
const SESSION_CHIME_DURATION = 0.07;
const SESSION_CHIME_GAP = 0.05;
const SESSION_CHIME_WAVE: OscillatorType = 'sawtooth';

interface DataVizSonificationPlayerProps {
  series: ResolvedChartSeries[];
  selectedSeriesId: string | undefined;
  onSelectSeries: (id: string) => void;
  xAxisType?: DataVizBlockData['xAxisType'];
  /**
   * Ids of the orientation sentences, which the BLOCK renders above the chart.
   *
   * They belong at the top — they introduce the chart — and rendered from in here they arrived
   * after it, with their read-through badges landing on this control row. The player still points
   * at them so a student who tabs to the play region hears them on focus.
   */
  orientationIds?: string[];
  /** Only for naming a series in the picker — the axis SENTENCES are built by `chartOrientation`. */
  yAxisLabel?: DataVizBlockData['yAxisLabel'];
  sonificationThresholds?: DataVizBlockData['sonificationThresholds'];
  /** Fires with the point currently sounding (or null once nothing is playing) — lets a sighted
   * parent draw a visual highlight in sync with the audio, for low-vision/sighted students. */
  onPlayingPointChange?: (point: { x: number | string; y: number } | null) => void;
  /** Fires every animation frame during playback with the CURRENT x-position implied by elapsed
   * time (or null when not playing) — independent of whether a point is actively sounding, so a
   * parent can render a moving playhead line that keeps moving through silent stretches (before
   * the first point, between clusters, after the last one) instead of looking frozen. Numeric x
   * only — a continuously-sliding position doesn't map cleanly onto discrete category ticks. */
  onPlayheadChange?: (x: number | null) => void;
}

function pitchForValue(y: number, minY: number, maxY: number): number {
  if (minY === maxY) return (LOW_HZ + HIGH_HZ) / 2;
  const t = (y - minY) / (maxY - minY);
  return LOW_HZ + t * (HIGH_HZ - LOW_HZ);
}

/** Exact interpolated times (not snapped to the nearest point) at which the value sequence crosses
 * `threshold` — e.g. values -10 then 10 crossing 0 gives the midpoint time; -10 then 1 gives a time
 * much closer to the second point's time, matching how far along the linear segment 0 actually
 * falls. This mirrors the straight-line segments the chart itself draws between points. */
function findThresholdCrossingTimes(values: number[], times: number[], threshold: number): number[] {
  const crossings: number[] = [];
  for (let i = 0; i < values.length - 1; i++) {
    const v1 = values[i]!, v2 = values[i + 1]!;
    if (v1 === threshold) { crossings.push(times[i]!); continue; }
    if ((v1 - threshold) * (v2 - threshold) < 0) {
      const frac = (threshold - v1) / (v2 - v1);
      crossings.push(times[i]! + frac * (times[i + 1]! - times[i]!));
    }
  }
  if (values.length > 0 && values[values.length - 1] === threshold) crossings.push(times[times.length - 1]!);
  return crossings;
}

export function DataVizSonificationPlayer({ series, selectedSeriesId, onSelectSeries, xAxisType, orientationIds = [], yAxisLabel, sonificationThresholds, onPlayingPointChange, onPlayheadChange }: DataVizSonificationPlayerProps) {
  const { t, i18n } = useTranslation();
  const instructionsId = useId();
  const audioCtxRef = useRef<AudioContext | null>(null);
  const activeNodesRef = useRef<{ osc: OscillatorNode; gain: GainNode }[]>([]);
  const rafRef = useRef<number | null>(null);
  const lastAnnouncedIndexRef = useRef(-1);
  const yCrossingsRef = useRef<number[]>([]);
  const xCrossingsRef = useRef<number[]>([]);
  const announcedYCrossingsRef = useRef<Set<number>>(new Set());
  const announcedXCrossingsRef = useRef<Set<number>>(new Set());
  const [playing, setPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const [speed, setSpeed] = useState(1);

  /**
   * A data value as it is SPOKEN.
   *
   * These were handed to i18n as raw numbers, so the string came from JS's own `toString` — which
   * switches to exponential above 1e21 and below 1e-6 without being asked. Chart data is exactly
   * where those live: Avogadro announced as "6.02e+23", read aloud as "six point oh two ee plus
   * twenty three". Routed through the shared formatter first so the spoken value matches the axis,
   * then through `speakNumber` so an exponent is said rather than spelled.
   *
   * Categorical x values are strings and pass through untouched.
   */
  function speakValue(v: number | string): string {
    return typeof v === 'number' ? speakNumber(fmtLocale(v, i18n.language), t) : v;
  }

  const playableSeries = series.filter((s) => s.points.length > 0);
  const selected = playableSeries.find((s) => s.id === selectedSeriesId) ?? playableSeries[0];

  // Playback always reads left-to-right along the x-axis, regardless of the order rows were
  // entered/reordered in the grid — "reading the graph" should mean x-order, not storage order.
  const orderedPoints = useMemo(() => {
    if (!selected) return [];
    const pts = selected.points;
    const numericX = pts.every((p) => typeof p.x === 'number');
    return numericX ? [...pts].sort((a, b) => (a.x as number) - (b.x as number)) : pts;
  }, [selected]);

  // The x-domain and total duration are computed across EVERY series, not just the one currently
  // selected — otherwise a short/narrow series (e.g. 5 points spanning 1-5) gets independently
  // stretched to fill the same playback duration as a long/wide one (e.g. 100 points spanning
  // 1-100), which sounds like it's spread across the full range instead of clustered at one end of
  // it. Scaling every series to this same shared timeline means a narrow series instead plays as a
  // proportionally short burst, correctly conveying where it sits relative to the others.
  const globalTiming = useMemo(() => {
    const playable = series.filter((s) => s.points.length > 0);
    const maxPointCount = Math.max(1, ...playable.map((s) => s.points.length));
    const allNumeric = playable.every((s) => s.points.every((p) => typeof p.x === 'number'));
    if (allNumeric) {
      const allX = playable.flatMap((s) => s.points.map((p) => p.x as number));
      return { numericX: true as const, minX: Math.min(...allX), maxX: Math.max(...allX), maxPointCount, categories: null as Array<number | string> | null };
    }
    // Categorical (or mixed) x — position by index within the shared category order across every
    // series (the same union the chart's own x-axis renders), so a series whose categories are a
    // narrow subset of the full set still lands in the right relative slice of the timeline.
    const categories = collectXValues(playable);
    return { numericX: false as const, minX: 0, maxX: Math.max(1, categories.length - 1), maxPointCount, categories };
  }, [series]);

  useEffect(() => () => {
    stopAll();
    audioCtxRef.current?.close();
  }, []);

  function getCtx(): AudioContext {
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
    return audioCtxRef.current;
  }

  function stopAll() {
    for (const { osc, gain } of activeNodesRef.current) {
      try { gain.gain.cancelScheduledValues(0); osc.stop(); } catch { /* already stopped */ }
    }
    activeNodesRef.current = [];
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }

  function scheduleNote(ctx: AudioContext, freq: number, startTime: number, duration: number, type: OscillatorType = 'sine') {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const attack = Math.min(0.02, duration / 4);
    const release = Math.min(0.03, duration / 4);
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.3, startTime + attack);
    gain.gain.linearRampToValueAtTime(0.3, startTime + duration - release);
    gain.gain.linearRampToValueAtTime(0, startTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration);
    activeNodesRef.current.push({ osc, gain });
  }

  /** A short, percussive click (fast attack, exponential decay) — layered on top of a data note
   * rather than replacing it, so the pitch (value) and the click (threshold crossing) are both
   * audible at once. */
  function scheduleClick(ctx: AudioContext, freq: number, startTime: number, duration: number, type: OscillatorType, peakGain: number) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(peakGain, startTime + duration * 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.01);
    activeNodesRef.current.push({ osc, gain });
  }

  /** Ascending (low-then-high) for "starting", descending (high-then-low) for "finished" — the
   * same pitch pair read in opposite directions, matching the common start/stop-sound convention. */
  function scheduleSessionChime(ctx: AudioContext, startAt: number, ascending: boolean) {
    const first = ascending ? SESSION_CHIME_LOW_HZ : SESSION_CHIME_HIGH_HZ;
    const second = ascending ? SESSION_CHIME_HIGH_HZ : SESSION_CHIME_LOW_HZ;
    scheduleClick(ctx, first, startAt, SESSION_CHIME_DURATION, SESSION_CHIME_WAVE, 0.25);
    scheduleClick(ctx, second, startAt + SESSION_CHIME_GAP, SESSION_CHIME_DURATION, SESSION_CHIME_WAVE, 0.25);
  }

  function announcePoint(index: number) {
    const point = orderedPoints[index];
    if (!point) return;
    setCurrentIndex(index);
    onPlayingPointChange?.({ x: point.x, y: point.y });
    const x = xAxisType === 'date' && typeof point.x === 'number' ? formatDateAxisValue(point.x) : speakValue(point.x);
    setAnnouncement(t('blocks.dataviz.sonification.pointAnnouncement', { index: index + 1, total: orderedPoints.length, x, y: speakValue(point.y) }));
  }

  function handlePlay() {
    const n = orderedPoints.length;
    if (n === 0) return;
    stopAll();
    const ctx = getCtx();
    const ys = orderedPoints.map((p) => p.y);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const numericX = globalTiming.numericX;
    const xs = numericX
      ? orderedPoints.map((p) => p.x as number)
      : orderedPoints.map((p) => globalTiming.categories!.indexOf(p.x));
    const minX = globalTiming.minX;
    const maxX = globalTiming.maxX;
    // Pegged to the longest series across the whole chart, not this series' own point count — so
    // switching which series is selected never itself changes how long a full playthrough takes,
    // and a short series just occupies less of that same fixed timeline (see globalTiming above).
    const totalDuration = Math.min(8, Math.max(3, 3 + Math.sqrt(globalTiming.maxPointCount) * 0.3)) / speed;
    const noteDuration = AUTO_PLAY_NOTE_DURATION / speed;
    const startTime = ctx.currentTime + 0.05;

    // Real x-position mapped to real time, not index — two points at (or near) the same x
    // genuinely play together/overlap, and uneven x-spacing (e.g. 1, 2.5, 2.6, 4) is audible as
    // uneven pacing, rather than every point getting an equal time slice regardless of x-distance.
    const noteStartOffsets = xs.map((x) => (maxX === minX ? 0 : ((x - minX) / (maxX - minX)) * totalDuration));

    orderedPoints.forEach((p, i) => {
      scheduleNote(ctx, pitchForValue(p.y, minY, maxY), startTime + noteStartOffsets[i]!, noteDuration);
    });
    scheduleSessionChime(ctx, startTime, true);
    scheduleSessionChime(ctx, startTime + totalDuration, false);

    yCrossingsRef.current = [];
    xCrossingsRef.current = [];
    announcedYCrossingsRef.current = new Set();
    announcedXCrossingsRef.current = new Set();
    if (sonificationThresholds?.y !== undefined) {
      const offsets = findThresholdCrossingTimes(ys, noteStartOffsets, sonificationThresholds.y);
      yCrossingsRef.current = offsets;
      offsets.forEach((offset) => {
        scheduleClick(ctx, Y_THRESHOLD_FREQ, startTime + offset, THRESHOLD_DURATION, Y_THRESHOLD_WAVE, 0.3);
      });
    }
    if (sonificationThresholds?.x !== undefined && numericX) {
      const offsets = findThresholdCrossingTimes(xs, noteStartOffsets, sonificationThresholds.x);
      xCrossingsRef.current = offsets;
      offsets.forEach((offset) => {
        scheduleClick(ctx, X_THRESHOLD_FREQ, startTime + offset, THRESHOLD_DURATION, X_THRESHOLD_WAVE, 0.3);
      });
    }

    setPlaying(true);
    setAnnouncement(t('blocks.dataviz.sonification.playbackStarted'));
    lastAnnouncedIndexRef.current = -1;
    const tick = () => {
      const elapsed = ctx.currentTime - startTime;
      if (elapsed >= totalDuration) {
        setPlaying(false);
        onPlayingPointChange?.(null);
        onPlayheadChange?.(null);
        setAnnouncement(t('blocks.dataviz.sonification.playbackFinished'));
        rafRef.current = null;
        return;
      }
      yCrossingsRef.current.forEach((offset, ci) => {
        if (elapsed >= offset && !announcedYCrossingsRef.current.has(ci)) {
          announcedYCrossingsRef.current.add(ci);
          setAnnouncement(t('blocks.dataviz.sonification.yThresholdCrossedAnnouncement'));
        }
      });
      xCrossingsRef.current.forEach((offset, ci) => {
        if (elapsed >= offset && !announcedXCrossingsRef.current.has(ci)) {
          announcedXCrossingsRef.current.add(ci);
          setAnnouncement(t('blocks.dataviz.sonification.xThresholdCrossedAnnouncement'));
        }
      });
      // A point is "active" only for its own noteDuration window — not from t=0 (a point scheduled
      // to start well into the timeline, e.g. a disjoint cluster, must stay dark until its own
      // start time) and not indefinitely after it ends (so the graph doesn't look stuck lit on the
      // last point through a long silent tail). Outside every window, the highlight clears.
      // When points are packed closer together than noteDuration (dense series), consecutive
      // windows overlap — always prefer the most recently started point over an earlier one whose
      // window merely hasn't closed yet, or the highlight visibly lags the playhead.
      let activeIdx = -1;
      for (let i = 0; i < n; i++) {
        const start = noteStartOffsets[i]!;
        if (start > elapsed) break;
        activeIdx = elapsed < start + noteDuration ? i : -1;
      }
      if (activeIdx !== -1 && activeIdx !== lastAnnouncedIndexRef.current) {
        lastAnnouncedIndexRef.current = activeIdx;
        announcePoint(activeIdx);
      } else if (activeIdx === -1 && lastAnnouncedIndexRef.current !== -1) {
        lastAnnouncedIndexRef.current = -1;
        onPlayingPointChange?.(null);
      }
      // The playhead moves continuously through the shared timeline regardless of whether a point
      // is actively sounding right now — this is what keeps silent stretches (before the first
      // point, between disjoint clusters, after the last one) reading as "still running" instead of
      // frozen. Numeric x only: a category axis has no continuous position to interpolate.
      if (numericX) {
        onPlayheadChange?.(minX + (elapsed / totalDuration) * (maxX - minX));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  function handlePause() {
    stopAll();
    setPlaying(false);
    onPlayingPointChange?.(null);
    onPlayheadChange?.(null);
  }

  /** On-demand only (never auto-played before every Play) — a listener who's already learned the
   * range for this data shouldn't have to sit through it again every time. Triangle wave (not the
   * data tones' sine) so it's never mistaken for the first two data points. */
  function handleCalibrate() {
    const ys = orderedPoints.map((p) => p.y);
    if (ys.length === 0) return;
    stopAll();
    setPlaying(false);
    onPlayingPointChange?.(null);
    onPlayheadChange?.(null);
    const ctx = getCtx();
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const start = ctx.currentTime + 0.05;
    scheduleNote(ctx, pitchForValue(minY, minY, maxY), start, CALIBRATION_TONE_DURATION, 'triangle');
    scheduleNote(ctx, pitchForValue(maxY, minY, maxY), start + CALIBRATION_TONE_DURATION + CALIBRATION_GAP, CALIBRATION_TONE_DURATION, 'triangle');
    setAnnouncement(t('blocks.dataviz.sonification.calibrationAnnouncement'));
  }

  function playSinglePoint(index: number) {
    const point = orderedPoints[index];
    if (!point) return;
    stopAll();
    setPlaying(false);
    const ctx = getCtx();
    const ys = orderedPoints.map((p) => p.y);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    scheduleNote(ctx, pitchForValue(point.y, minY, maxY), ctx.currentTime + 0.02, MANUAL_PREVIEW_DURATION);
    announcePoint(index);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    const n = orderedPoints.length;
    if (n === 0) return;
    if (e.key === ' ') {
      e.preventDefault();
      if (playing) handlePause(); else handlePlay();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      playSinglePoint(Math.max(0, currentIndex - 1));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      playSinglePoint(Math.min(n - 1, currentIndex + 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      playSinglePoint(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      playSinglePoint(n - 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      stopAll();
      setPlaying(false);
      setCurrentIndex(0);
      onPlayingPointChange?.(null);
      onPlayheadChange?.(null);
    }
  }

  /**
   * Play a reference line's own click, on demand.
   *
   * A page cannot make a screen reader play a sound as part of an announcement — speech and page
   * audio are separate channels, and nothing in ARIA bridges them. So the announcement says a
   * reference line is there, and this plays the exact tone it will make during playback: the same
   * frequency, waveform and duration `scheduleClick` uses for that axis, so what you hear here is
   * what you will recognise later rather than a stand-in for it.
   *
   * Same shape as Calibrate, which already exists to let someone hear the pitch range before
   * playing anything.
   */
  function previewReference(axis: 'x' | 'y') {
    const ctx = getCtx();
    void ctx.resume();
    scheduleClick(
      ctx,
      axis === 'y' ? Y_THRESHOLD_FREQ : X_THRESHOLD_FREQ,
      ctx.currentTime + 0.02,
      THRESHOLD_DURATION,
      axis === 'y' ? Y_THRESHOLD_WAVE : X_THRESHOLD_WAVE,
      0.3,
    );
  }

  if (!selected) return null;

  /**
   * A landmark button: what it plays, in words as well as a glyph.
   *
   * These were three near-identical circles. The icons are honest — a flat dash for the horizontal
   * y reference, an upright one for the vertical x reference, a ruler for the pitch range — but an
   * icon that only reads once you already know is not a label. These are rare-use controls beside a
   * play button that is pressed constantly, so they can afford the width that play cannot, and the
   * `title` stays for the narrow case where the text wraps away.
   */
  const landmarkButton = (key: string, icon: React.ReactNode, label: string, text: string, onClick: () => void) => (
    <Button size='sm' key={key} onClick={onClick} aria-label={label} title={label} /* The task editor's own small labelled button, unmodified — the same string some twenty other controls use. A borderless pill was tried here first, borrowed from the Sound block's transport; it exists nowhere else in the app, and one-off styling on a control nobody recognises makes it harder to place, not easier. The play button beside these stays round and filled, so shape now separates the two jobs. */ className='shrink-0'>
      {icon}
      <span aria-hidden='true' className='whitespace-nowrap'>{text}</span>
    </Button>
  );

  /**
   * Each landmark says WHICH VALUE it will play, not just that it will play one.
   *
   * "Hear the y reference sound" tells a listener a sound exists and nothing about what it marks —
   * so they press it, hear a click, and still have to go looking for the number. The number is the
   * whole point of a reference line, and a sighted student reads it straight off the chart.
   *
   * The range button quotes the SELECTED SERIES' own min and max, because those are literally the
   * two notes `handleCalibrate` plays. Not the axis bounds, which are rounded outwards and would
   * name two values the button never sounds.
   */
  const ys = orderedPoints.map(p => p.y);
  const sayX = (v: number) => (xAxisType === 'date' ? formatDateAxisValue(v) : speakValue(v));
  const landmarks = [
    ...(ys.length ? [landmarkButton(
      'range',
      <Ruler aria-hidden='true' className='h-3.5 w-3.5' />,
      t('blocks.dataviz.sonification.calibrate', {
        min: speakValue(Math.min(...ys)),
        max: speakValue(Math.max(...ys)),
      }),
      t('blocks.dataviz.sonification.calibrateShort'),
      handleCalibrate,
    )] : []),
    ...(sonificationThresholds?.y != null ? [landmarkButton(
      'refY',
      <Minus aria-hidden='true' className='h-3.5 w-3.5' />,
      t('blocks.dataviz.sonification.hearReferenceY', { value: speakValue(sonificationThresholds.y) }),
      t('blocks.dataviz.sonification.hearReferenceYShort'),
      () => previewReference('y'),
    )] : []),
    ...(sonificationThresholds?.x != null ? [landmarkButton(
      'refX',
      <Minus aria-hidden='true' className='h-3.5 w-3.5 rotate-90' />,
      // A date x-axis stores epoch-ms, so the raw number would announce as a fourteen-digit
      // integer. The same formatter the axis ticks use.
      t('blocks.dataviz.sonification.hearReferenceX', { value: sayX(sonificationThresholds.x) }),
      t('blocks.dataviz.sonification.hearReferenceXShort'),
      () => previewReference('x'),
    )] : []),
  ];

  return (
    <div className='flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2'>
      {/*
        Three groups across three columns, because the row had grown three different jobs.

        LANDMARKS teach you the terrain before you start — what the pitch range sounds like, and
        what each reference click sounds like. TRANSPORT operates the playback. SETTINGS change how
        it plays. Undivided, they read as one line of interchangeable circles, and a group with no
        name is no help to a listener either: a reader now announces "Hear the chart's landmarks,
        group" on the way in, which is the sentence that makes three unfamiliar buttons make sense.

        Landmarks left, everything that OPERATES the playback right. Play was centred for a while,
        which cost the left column half the row and wrapped the three landmark buttons onto two
        lines the moment they carried real labels. Sitting Play beside the speed it applies to is
        both tidier and truer: they are one control surface, not two.
      */}
      <div role='group' aria-label={t('blocks.dataviz.sonification.landmarksGroup')} className='flex shrink-0 items-center gap-1.5'>
        {/*
          A VISIBLE caption, and `aria-hidden` on purpose.

          It exists so a sighted teacher knows what three unfamiliar buttons are for without
          hovering each one — the group's `aria-label` already does that job for a listener, and
          better, since it can afford the whole sentence. Reading both would announce "Hear" and
          then "Hear the chart's landmarks". The same split the sensor simulate bar's side labels
          and the slider's handle captions already use.
        */}
        <span aria-hidden='true' className='shrink-0 text-xs text-ws-muted'>
          {t('blocks.dataviz.sonification.landmarksCaption')}
        </span>
        {landmarks}
      </div>
      <div className='flex shrink-0 items-center gap-2'>
        <div role='group' aria-label={t('blocks.dataviz.sonification.transportGroup')} className='flex items-center'>
          <button
            type='button'
            onClick={playing ? handlePause : handlePlay}
            aria-label={playing ? t('blocks.dataviz.sonification.pause') : t('blocks.dataviz.sonification.play')}
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary hover:opacity-90 disabled:opacity-50'
          >
            {playing ? <Pause className='h-3.5 w-3.5' /> : <Play className='h-3.5 w-3.5 translate-x-[1px]' />}
          </button>
        </div>

      {/* The settings are their own group, and that is what lets Play sit dead centre: with the
          two selects beside it inside one transport group, the button's position depended on how
          wide the series names happened to be. Three columns, so the middle one is the middle of
          the chart whatever the sides hold. */}
      <div role='group' aria-label={t('blocks.dataviz.sonification.settingsGroup')} className='flex items-center justify-end gap-2'>
        <SelectMenu
          value={String(speed)}
          onChange={v => setSpeed(Number(v))}
          options={playbackSpeedOptions()}
          ariaLabel={t('blocks.dataviz.sonification.speedLabel')}
          className='w-fit' />

        {playableSeries.length > 1 && (
          <SelectMenu
            value={selected.id}
            onChange={v => { stopAll(); setPlaying(false); onSelectSeries(v); }}
            options={playableSeries.map(sr => ({
              value: sr.id,
              label: seriesDisplayLabel(sr, series.findIndex(orig => orig.id === sr.id), series.length, yAxisLabel, t),
            }))}
            ariaLabel={t('blocks.dataviz.sonification.seriesSelectorLabel')}
            className='w-fit' />
        )}
        </div>
      </div>

      {/* Keyboard-operable region (Space/Arrow/Home/End/Esc) — sr-only rather than a visible
          instructional paragraph: sighted users get the same play/pause + step behavior just by
          using the button, the full instructions only need to reach screen-reader users, not take
          up visible space for everyone.

          The instructions are a DESCRIPTION, not the name. As the name they were re-read in full
          on every arrow keypress, because a reader re-announces name and value together when a
          slider's value changes — so stepping through twelve points meant hearing thirty words of
          key bindings twelve times, with the point buried at the end each time. A description is
          announced once, on focus, which is when instructions are actually wanted. */}
      <p id={instructionsId} className='sr-only'>{t('blocks.dataviz.sonification.playRegionInstructions')}</p>
      <div
        role='slider'
        tabIndex={0}
        aria-label={t('blocks.dataviz.sonification.playRegionLabel')}
        aria-describedby={[...orientationIds, instructionsId].join(' ')}
        aria-valuemin={0}
        aria-valuemax={orderedPoints.length - 1}
        aria-valuenow={currentIndex}
        /**
         * Before anything has played, the value is HOW MANY points there are — not point one.
         *
         * It used to fall back to a full announcement of the first point, which read as "Chart
         * sound player, slider, point 1 of 177" and then nothing else: a frozen snapshot standing
         * in for a series of 177. It told a listener about one datum they had not asked for and
         * nothing about the control. The size of the thing is what is useful before you start; the
         * points themselves arrive as you step, which is what the arrow keys are for.
         */
        aria-valuetext={announcement || t('blocks.dataviz.sonification.pointCount', { count: orderedPoints.length })}
        onKeyDown={handleKeyDown}
        className='sr-only'
      />

      <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>
    </div>
  );
}
